// The signed-in shell for Radish staff and resellers outside a location (legacy AppLayout with `menuItemsAdmin` /
// `menuItemsReseller` in AppSidebar2.vue; field note backoffice.accounts.list): the dark sidebar with the staff menu and
// no location switcher, the toolbar title, the theme menu (dev and staging only, as legacy) and the user menu. Legacy's staff menu also lists
// QR Templates, Live Updates, Prompt Catalog, AI Image Templates and Menu Board Templates (a reseller's: QR
// Templates); each joins this list when its page is built (parity checklist admin-reseller rows). Activate a Device
// (SCR-023) is in both menus, as legacy keeps it in menuItemsAdmin and menuItemsReseller; Users (SCR-011/012) is the
// admin menu's only, for Radish admins.
import type { ReactNode } from 'react';
import { Link, useRouter, useRouterState } from '@tanstack/react-router';
import { DropdownMenu } from 'radix-ui';
import { TabletSmartphone } from 'lucide-react';
import { AppFrame, type Appearance, type NavNode } from '@radish/ui-web';
import type { SessionView } from '@radish/contracts';
import { choosingAccount, endSession, initialsOf, sessionSnapshot } from '../session/session.ts';
import { useAppearance } from './appearance.tsx';
import { APPEARANCES, HEADER_ACTION, ThemeMenu, showsThemeMenu } from './header-actions.tsx';


const ITEM = 'flex h-9 cursor-pointer items-center px-4 outline-none data-[highlighted]:bg-menu-item-hover';

function StaffUserMenu() {
  const { appearance, setAppearance } = useAppearance();
  const router = useRouter();
  const user = (choosingAccount() ?? sessionSnapshot()?.session ?? null)?.user;
  if (user === undefined) return null;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger aria-label="Account menu" data-testid="user-menu" className={`${HEADER_ACTION} text-[16px] font-semibold tracking-[0.02em]`}>
        {initialsOf(user.name)}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={6} className="z-[3000] min-w-56 rounded-q border border-line bg-menu py-1 text-[14px] text-menu-ink-body shadow-lg dark:border-transparent dark:shadow-none">
          <div className="px-4 py-2" data-testid="user-menu-identity">
            <div className="font-medium text-text-1">{user.name}</div>
            <div className="text-[12px] text-text-3">{user.email}</div>
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          <DropdownMenu.Label className="px-4 py-2 text-[12px] text-text-3">Appearance</DropdownMenu.Label>
          <DropdownMenu.RadioGroup value={appearance} onValueChange={(v) => setAppearance(v as Appearance)}>
            {APPEARANCES.map((a) => (
              <DropdownMenu.RadioItem key={a.value} value={a.value} className="flex h-9 cursor-pointer items-center px-4 outline-none data-[highlighted]:bg-menu-item-hover data-[state=checked]:font-medium data-[state=checked]:text-primary">
                {a.label}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          <DropdownMenu.Item
            className={ITEM}
            onSelect={() => {
              // Legacy's Sign Out pushes '/login?' + Date.now(), a bare ?<ms> with no key (AppHeaderUserMenu.vue; Q-NAV-16).
              // Pushed on the history as is: the router's own navigate would write it ?<ms>=.
              void endSession().then(() => router.history.push(`/login?${String(Date.now())}`));
            }}
          >
            Sign Out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/**
 * Which staff menu a signed-in person gets (legacy updateMenu switches whole arrays by user type): Radish staff get the
 * admin menu, a reseller the reseller menu, anyone else none (they never see the staff shell's pages).
 */
export function staffMenuOf(view: SessionView): 'admin' | 'reseller' | null {
  if (view.user.staffRole !== null) return 'admin';
  return view.accounts.some((a) => a.via === 'reseller') ? 'reseller' : null;
}

/**
 * Legacy's staff sidebar glyphs (AppSidebarIcon.vue, B2049): its own filled person-in-a-circle for Accounts and Users
 * (components/icons/sidebar/MenuCustomers.vue) and Material's filled qr_code for QR Templates (MenuQRTemplates.vue), each
 * drawn 17 px wide by `.app-sidebar-icon` (29 px less its 12 px of padding). The lucide entries are 17 px at stroke 1.5.
 */
const LEGACY_PERSON = 'M10,20A10,10,0,1,1,20,10,10,10,0,0,1,10,20Zm0-4a8.3929,8.3929,0,0,0-3.851,1.009,7.9758,7.9758,0,0,0,7.7,0A8.4,8.4,0,0,0,10,16Zm0-2a10.6934,10.6934,0,0,1,5.6,1.7,8,8,0,1,0-11.2,0A10.72,10.72,0,0,1,10,14Zm0-2a4,4,0,1,1,4-4A4,4,0,0,1,10,12Zm0-6a2,2,0,1,0,2,2A2,2,0,0,0,10,6Z';
const MATERIAL_QR_CODE = 'M80-680v-200h200v80H160v120H80Zm0 600v-200h80v120h120v80H80Zm600 0v-80h120v-120h80v200H680Zm120-600v-120H680v-80h200v200h-80ZM700-260h60v60h-60v-60Zm0-120h60v60h-60v-60Zm-60 60h60v60h-60v-60Zm-60 60h60v60h-60v-60Zm-60-60h60v60h-60v-60Zm120-120h60v60h-60v-60Zm-60 60h60v60h-60v-60Zm-60-60h60v60h-60v-60Zm240-320v240H520v-240h240ZM440-440v240H200v-240h240Zm0-320v240H200v-240h240Zm-60 500v-120H260v120h120Zm0-320v-120H260v120h120Zm320 0v-120H580v120h120Z';
const personGlyph = () => <svg viewBox="0 0 20 20" width="17" height="17" fill="currentColor" aria-hidden><path d={LEGACY_PERSON} /></svg>;
const qrGlyph = () => <svg viewBox="0 -960 960 960" width="17" height="17" fill="currentColor" aria-hidden><path d={MATERIAL_QR_CODE} /></svg>;

/** The staff menu: the pages built so far, current by path (legacy highlights Accounts on its detail pages too). */
function staffNav(pathname: string, radishAdmin: boolean): NavNode[] {
  return [
    { id: 'staff.accounts', label: 'Accounts', href: '/accounts', icon: personGlyph(), active: pathname === '/accounts' || pathname.startsWith('/accounts/') },
    // SCR-011/012: legacy menuItemsAdmin only (REQUIRES_ADMIN), so Radish admins, never a reseller or support staff.
    ...(radishAdmin ? [{ id: 'staff.users', label: 'Users', href: '/users/list', icon: personGlyph(), active: pathname.startsWith('/users') }] : []),
    // SCR-024: legacy lists QR Templates for admins and resellers alike (both menus; REQUIRES_ADMIN_OR_RESELLER). The
    // reseller's entry read "Accounts" (B904): both read QR Templates here.
    { id: 'staff.qr-templates', label: 'QR Templates', href: '/qr-templates', icon: qrGlyph(), active: pathname === '/qr-templates' },
    { id: 'staff.device-claims', label: 'Activate a Device', href: '/device-claims', icon: <TabletSmartphone size={17} strokeWidth={1.5} aria-hidden />, active: pathname === '/device-claims' },
  ];
}

export function StaffShell({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <AppFrame
      logoSrc="/radish-logo-dark.svg"
      nav={staffNav(pathname, (choosingAccount() ?? sessionSnapshot()?.session ?? null)?.user.staffRole === 'admin')}
      renderLink={(node, className, content) => (
        <Link to={(node.href ?? '/accounts') as '/accounts' | '/device-claims' | '/users/list' | '/qr-templates'} className={className} aria-current={node.active ? 'page' : undefined}>{content}</Link>
      )}
      title={title}
      routeKey={pathname}
      toolbarEnd={
        <>
          {showsThemeMenu(window.location.hostname, import.meta.env.DEV) ? <ThemeMenu /> : null}
          <StaffUserMenu />
        </>
      }
    >
      {children}
    </AppFrame>
  );
}
